import { useState } from 'preact/hooks'
import { AlertTriangle, Check, Eye, EyeOff, Info, Loader2, Plus, X } from 'lucide-preact'
import type { WebinarRoomPollViewDto } from '@/api/webinarRoom'
import { Select } from '@/components/common/Select'
import { TextBox } from '@/components/common/TextBox'
import { POLL_STAY_CHOICES, stayFromValue, stayLabel, stayValue } from '@/webinarRoom/polls/pollStay'
import { RoomPollPreview } from './RoomPollPreview'
import {
    POLL_MAX_OPTIONS, POLL_MIN_OPTIONS, POLL_OPTION_MAX_LENGTH, POLL_QUESTION_MAX_LENGTH, checkPollDraft, draftChanged, formatPollTime, lastPollNote,
    parsePollTime, type PollDraft, type PollDraftCheck, type PollTimeline,
} from './roomPolls'
import { BUTTON, BUTTON_GHOST, BUTTON_PRIMARY, NOTE } from './roomStyles'

const STAY_OPTIONS = POLL_STAY_CHOICES.map(seconds => ({ value: stayValue(seconds), label: stayLabel(seconds) }))

interface RoomPollFormProps {
    initial: PollDraft
    isNew: boolean
    /** The web's other polls, for the closeness warning; the poll being edited is left out. */
    others: readonly WebinarRoomPollViewDto[]
    timeline: PollTimeline | null
    /** The session's length, for when a last poll left up until the next one comes down; null before it has times. */
    sessionSeconds: number | null
    saving: boolean
    accent: string | null
    /** The typed time as seconds, or null, for the timeline's ghost marker. */
    onTimeChange: (seconds: number | null) => void
    onSave: (draft: PollDraft, check: PollDraftCheck) => void
    onCancel: () => void
}

interface Touched { time: boolean; question: boolean; options: boolean }

/** Writing a poll ahead: when it pops up, the question, how long it stays up, and two to four options. Opens under the timeline or in place. */
export function RoomPollForm({ initial, isNew, others, timeline, sessionSeconds, saving, accent, onTimeChange, onSave, onCancel }: RoomPollFormProps) {
    const [draft, setDraft] = useState<PollDraft>(initial)
    // An edit starts from a poll that already checks out, so its messages show straight away.
    const [touched, setTouched] = useState<Touched>({ time: !isNew, question: !isNew, options: !isNew })
    const [previewOpen, setPreviewOpen] = useState(false)
    const check = checkPollDraft(draft, others, timeline)
    const changed = draftChanged(draft, initial)
    const untilTheEnd = lastPollNote(draft, check, others, sessionSeconds)

    const setTime = (time: string) => {
        setDraft(current => ({ ...current, time }))
        // A complete mm:ss is judged as it is typed; anything less waits for the field to be left.
        if (parsePollTime(time) !== null) setTouched(current => ({ ...current, time: true }))
        onTimeChange(parsePollTime(time))
    }
    const setOption = (index: number, value: string) =>
        setDraft(current => ({ ...current, options: current.options.map((option, at) => (at === index ? value : option)) }))
    const removeOption = (index: number) => {
        setDraft(current => ({ ...current, options: current.options.filter((_, at) => at !== index) }))
        setTouched(current => ({ ...current, options: true }))
    }
    const addOption = () => setDraft(current => ({ ...current, options: [...current.options, ''] }))

    const save = () => {
        setTouched({ time: true, question: true, options: true })
        if (check.ok) onSave(draft, check)
    }

    const previewOptions = draft.options
        .map((option, index) => option.trim() || (index < POLL_MIN_OPTIONS ? `Option ${index + 1}` : ''))
        .filter(Boolean)

    return (
        <div className="my-3 rounded-xl bg-slate-50 dark:bg-slate-900/40 p-[18px]" role="group" aria-labelledby="room-poll-form-title">
            <p id="room-poll-form-title" className="mb-3.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{isNew ? 'New poll' : 'Edit poll'}</p>
            <div className="grid gap-3.5 sm:grid-cols-[132px_minmax(0,1fr)]">
                <TextBox
                    name="room-poll-time"
                    label="Pops up at"
                    required
                    value={draft.time}
                    onChange={setTime}
                    onBlur={() => setTouched(current => ({ ...current, time: true }))}
                    placeholder="mm:ss"
                    autoComplete="off"
                    autoFocus={isNew}
                    className="tabular-nums"
                    error={touched.time ? check.timeError ?? undefined : undefined}
                    helperText={timeline ? `Any time before ${formatPollTime(timeline.limitSeconds)}` : undefined}
                />
                <TextBox
                    name="room-poll-question"
                    label="Question"
                    required
                    value={draft.question}
                    onChange={question => setDraft(current => ({ ...current, question }))}
                    onBlur={() => setTouched(current => ({ ...current, question: true }))}
                    placeholder="What do you want to ask the room?"
                    maxLength={POLL_QUESTION_MAX_LENGTH}
                    autoComplete="off"
                    autoFocus={!isNew}
                    error={touched.question ? check.questionError ?? undefined : undefined}
                />
            </div>

            <Select
                name="room-poll-stays-up"
                label="Stays up for"
                containerClassName="mt-[18px] max-w-[220px]"
                options={STAY_OPTIONS}
                value={stayValue(draft.staysUpSeconds)}
                onChange={(event: Event) => {
                    const staysUpSeconds = stayFromValue((event.currentTarget as HTMLSelectElement).value)
                    setDraft(current => ({ ...current, staysUpSeconds }))
                }}
            />
            {untilTheEnd && (
                <p className={NOTE}>
                    <Info className="w-3.5 h-3.5 mt-0.5 flex-none text-slate-400" />
                    <span>{untilTheEnd}</span>
                </p>
            )}

            <fieldset className="mt-[18px]">
                <legend className="mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                    Options <span className="text-red-500">*</span>
                    <span className="ml-2 font-normal text-slate-500 dark:text-slate-400">{POLL_MIN_OPTIONS} to {POLL_MAX_OPTIONS}</span>
                </legend>
                <div className="flex flex-col gap-2">
                    {draft.options.map((option, index) => (
                        <div key={index} className="grid grid-cols-[20px_minmax(0,1fr)_auto] items-center gap-2">
                            <span aria-hidden="true" className="text-right text-xs tabular-nums text-slate-500 dark:text-slate-400">{index + 1}</span>
                            <TextBox
                                name={`room-poll-option-${index}`}
                                ariaLabel={`Option ${index + 1}`}
                                value={option}
                                onChange={value => setOption(index, value)}
                                onBlur={() => setTouched(current => ({ ...current, options: true }))}
                                placeholder={`Option ${index + 1}`}
                                maxLength={POLL_OPTION_MAX_LENGTH}
                                autoComplete="off"
                            />
                            {draft.options.length > POLL_MIN_OPTIONS
                                ? (
                                    <button type="button" className={BUTTON_GHOST} aria-label={`Remove option ${index + 1}`} title="Remove option" onClick={() => removeOption(index)}>
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                )
                                : <span />}
                        </div>
                    ))}
                </div>
                {draft.options.length < POLL_MAX_OPTIONS && (
                    <button type="button" className={`${BUTTON_GHOST} mt-2 ml-5`} onClick={addOption}><Plus className="w-3.5 h-3.5" />Add option</button>
                )}
                {touched.options && check.optionsError && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{check.optionsError}</p>}
            </fieldset>

            {check.warning && (
                <p className="mt-3.5 flex items-start gap-2 text-sm text-amber-800 dark:text-amber-300">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-none text-amber-600 dark:text-amber-400" />
                    <span>{check.warning}</span>
                </p>
            )}

            <div className="mt-[18px] flex flex-wrap items-center gap-2">
                <button type="button" className={BUTTON_PRIMARY} disabled={!check.ok || !changed || saving} onClick={save}>
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    {isNew ? 'Add poll' : 'Save'}
                </button>
                <button type="button" className={BUTTON} disabled={saving} onClick={onCancel}><X className="w-3.5 h-3.5" />Cancel</button>
                <button type="button" className={`${BUTTON_GHOST} ml-auto`} aria-expanded={previewOpen} onClick={() => setPreviewOpen(open => !open)}>
                    {previewOpen ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    {previewOpen ? 'Hide preview' : 'Preview as viewers see it'}
                </button>
            </div>
            {previewOpen && (
                <div className="mt-4">
                    <RoomPollPreview question={draft.question.trim() || 'Your question'} options={previewOptions} offsetSeconds={check.offsetSeconds} staysUpSeconds={draft.staysUpSeconds} accent={accent} />
                </div>
            )}
        </div>
    )
}
